/**
 * Der lokale Datenbestand.
 *
 * Grundsatz aus Kapitel B: Alle Bearbeitung geht gegen diese Datenbank. Die
 * Anwendung fragt nie einen Server, um eine Eingabe entgegenzunehmen. Wer hier
 * einen await auf fetch einbaut, hat den Offline-Betrieb aufgegeben.
 */

import { DATUMSFELDER, tagVon } from '../datum.ts';
import Dexie, { type Table } from 'dexie';

import type { Gebaeude } from './gebaeude.ts';
import type { Gelaendedaten } from './hoehendaten.ts';
import type {
  Artikel,
  Ausgangseintrag,
  Belegung,
  Einstellung,
  Flaeche,
  Foto,
  Kachelstand,
  Kartenmarke,
  Projektschritt,
  Dokument,
  Komponente,
  Konflikt,
  Leitungsweg,
  Materialposition,
  Materialsatz,
  Modul,
  Projekt,
  Projektkomponente,
  Stringzeile,
  Angebotszeile,
  Auftragszeile,
  Checklistenvorlage,
  Pruefbefundzeile,
  Projekteinstellung,
  Verschattungsobjektzeile,
  Wirtschaftlichkeitsdaten,
} from '../typen.ts';

export interface Zustandseintrag {
  schluessel: string;
  wert: unknown;
}

export class PvDatenbank extends Dexie {
  projekte!: Table<Projekt, string>;
  flaechen!: Table<Flaeche, string>;
  belegungen!: Table<Belegung, string>;
  module!: Table<Modul, string>;
  fotos!: Table<Foto, string>;
  fotodateien!: Table<{ foto_id: string; blob: Blob }, string>;
  einstellungen!: Table<Einstellung, string>;
  ausgang!: Table<Ausgangseintrag, number>;
  konflikte!: Table<Konflikt, number>;
  kachelstand!: Table<Kachelstand, string>;
  zustand!: Table<Zustandseintrag, string>;

  // Je Projekt abgelegt, Schluessel [projekt_id, id]. Eigene Tabelle seit
  // Version 9 — siehe dort, warum nicht einfach "gebaeude" umgebaut.
  gebaeudeumrisse!: Table<Gebaeude, [string, string]>;
  hoehendaten!: Table<Gelaendedaten, string>;
  komponenten!: Table<Komponente, string>;
  artikel!: Table<Artikel, string>;
  materialsaetze!: Table<Materialsatz, string>;
  projekt_komponenten!: Table<Projektkomponente, string>;
  strings!: Table<Stringzeile, string>;
  leitungswege!: Table<Leitungsweg, string>;
  materialpositionen!: Table<Materialposition, string>;
  wirtschaftlichkeit!: Table<Wirtschaftlichkeitsdaten, string>;
  verschattungsobjekte!: Table<Verschattungsobjektzeile, string>;
  kartenmarken!: Table<Kartenmarke, string>;
  projektschritte!: Table<Projektschritt, string>;
  dokumente!: Table<Dokument, string>;
  dokumentdateien!: Table<{ dokument_id: string; blob: Blob }, string>;
  angebote!: Table<Angebotszeile, string>;
  auftraege!: Table<Auftragszeile, string>;
  pruefbefunde!: Table<Pruefbefundzeile, string>;
  checklistenvorlagen!: Table<Checklistenvorlage, string>;
  projekteinstellungen!: Table<Projekteinstellung, string>;

  constructor(name = 'pv-planung') {
    super(name);
    // Version 1. Beim Erweitern eine neue Version anhaengen, nie diese aendern —
    // sonst verliert ein Geraet beim Update seinen Bestand (PV-B23).
    // Auf Wahrheitswerte wird bewusst nicht indiziert: IndexedDB kann true und
    // false nicht als Schluessel fuehren, ein solcher Index waere tote Last.
    // Fuer "schmutzig", "aktiv" und "hochgeladen" wird gefiltert.
    this.version(1).stores({
      projekte: 'id, kunde_name, ort, status',
      flaechen: 'id, projekt_id',
      belegungen: 'id, flaeche_id',
      module: 'id',
      fotos: 'id, projekt_id',
      fotodateien: 'foto_id',
      einstellungen: 'schluessel',
      ausgang: '++nummer, [tabelle+id]',
      konflikte: '++id, tabelle',
      kachelstand: 'projekt_id',
      zustand: 'schluessel',
    });

    // Version 2: Gebaeudeumrisse fuer den Flaechenvorschlag (PV-D18). Angehaengt,
    // nicht in Version 1 hineingeschrieben — sonst waere der Bestand der Geraete
    // im Feld beim Update weg.
    this.version(2).stores({
      gebaeude: 'id, projekt_id',
    });

    // Version 3: Stufe 2 — Verstringung, Komponenten, Leitungswege, Material.
    this.version(3).stores({
      komponenten: 'id, art',
      artikel: 'id, warengruppe',
      materialsaetze: 'id, [dachtyp+ausrichtung]',
      projekt_komponenten: 'id, projekt_id',
      strings: 'id, projekt_id',
      leitungswege: 'id, projekt_id',
      materialpositionen: 'id, projekt_id',
    });

    // Version 4: Stufe 3 und 4 — Wirtschaftlichkeit, Angebot, Abwicklung,
    // dazu die Verschattungsobjekte.
    this.version(4).stores({
      wirtschaftlichkeit: 'id, projekt_id',
      verschattungsobjekte: 'id, projekt_id',
      angebote: 'id, projekt_id, nummer',
      auftraege: 'id, projekt_id',
      pruefbefunde: 'id, projekt_id',
      checklistenvorlagen: 'id, auftragsart',
    });

    // Version 5: Einstellungen je Projekt. Stammdaten sind ab jetzt am Geraet
    // pflegbar — wer darf, entscheidet der Server.
    this.version(5).stores({
      projekteinstellungen: 'id, projekt_id, [projekt_id+schluessel]',
    });

    // Version 6: Gelaendedaten je Projekt (Kapitel N). Wie immer angehaengt —
    // wer eine bestehende Version aendert, kostet jedem Geraet im Feld seinen
    // lokalen Bestand.
    this.version(6).stores({
      hoehendaten: 'projekt_id',
    });

    // Version 7: Konflikte bekommen einen zusammengesetzten Schluessel.
    // Vorher legte jeder Abgleichversuch denselben Konflikt noch einmal an —
    // aus drei offenen Aenderungen wurden nach elf Versuchen 34 Konflikte.
    // Angehaengt, nicht geaendert: Wer Version 6 anfasst, kostet jedem Geraet
    // im Feld seinen Bestand (PV-B23).
    this.version(7).stores({
      konflikte: '++id, tabelle, [tabelle+datensatz_id]',
    });

    // Version 9: Gebaeude je Projekt, in einer neuen Tabelle.
    //
    // Hier stand eine Version 8, die den Primaerschluessel von "gebaeude" von
    // "id" auf "[projekt_id+id]" umstellen sollte. Das kann Dexie nicht — es
    // wirft "Not yet support for changing primary key", das Oeffnen schlaegt
    // fehl, und die Anwendung blieb auf "Einen Moment …" stehen. Vom
    // 15.09. bis 21.10.2026 lief so jedes Geraet, das die Anwendung vorher
    // schon hatte. Ein frisch installiertes lief, und deshalb ist es beim
    // Ausprobieren nicht aufgefallen.
    //
    // Version 8 ist deshalb entfernt — die Ausnahme von der Regel "nur
    // anhaengen", weil sie auf fast keinem Geraet je durchgelaufen ist. Dexie
    // vergleicht beim Upgrade gegen den tatsaechlichen Bestand, nicht gegen die
    // vorige Erklaerung. Damit kommen beide Staende hier an:
    //
    //   Version 7: "gebaeude" mit Schluessel "id" wird kopiert und geloescht.
    //   Version 8 (frisch installiert): "gebaeude" mit zusammengesetztem
    //   Schluessel wird genauso kopiert und geloescht.
    //
    // Geloescht wird erst nach dem Kopieren; Dexie haelt entfernte Tabellen
    // fuer die Upgrade-Funktion noch offen. Wer einen Schluessel aendern will:
    // immer so, ueber eine neue Tabelle. Der Test in test/datenbank.test.ts
    // spielt jeden Stand durch, den es im Feld geben kann.
    this.version(9)
      .stores({
        gebaeude: null,
        gebaeudeumrisse: '[projekt_id+id], projekt_id',
      })
      .upgrade(async (tx) => {
        const alte = await tx.table('gebaeude').toArray();
        if (alte.length > 0) await tx.table('gebaeudeumrisse').bulkPut(alte);
      });

    // Bemaßungen und Markierungen auf der Karte (11/2026). Nur angehängt.
    this.version(10).stores({ kartenmarken: 'id, projekt_id' });

    // Abwicklung: Schritte mit Erinnerung, Dokumente mit eigener Warteschlange
    // für die Dateien — wie bei den Fotos (11/2026). Nur angehängt.
    this.version(11).stores({
      projektschritte: 'id, projekt_id',
      dokumente: 'id, projekt_id',
      dokumentdateien: 'dokument_id',
    });

    // Version 12: Datumsfelder, die ein Server vor 0.17 als Zeitstempel
    // geliefert hat, auf „JJJJ-MM-TT“ bringen (siehe datum.ts). Ohne das
    // blieben sie auf jedem Gerät falsch, bis jemand die Zeile ändert — und
    // ein Datumsfeld, das leer erscheint, löscht beim Durchtippen das Datum.
    this.version(12)
      .stores({})
      .upgrade(async (tx) => {
        for (const [tabelle, felder] of Object.entries(DATUMSFELDER)) {
          await tx
            .table(tabelle)
            .toCollection()
            .modify((zeile: Record<string, unknown>) => {
              for (const feld of felder) {
                if (zeile[feld] != null) zeile[feld] = tagVon(zeile[feld]);
              }
            });
        }
      });
  }
}

export const db = new PvDatenbank();

export async function leseZustand<T>(schluessel: string, vorgabe: T): Promise<T> {
  const eintrag = await db.zustand.get(schluessel);
  return eintrag ? (eintrag.wert as T) : vorgabe;
}

export async function schreibeZustand(schluessel: string, wert: unknown): Promise<void> {
  await db.zustand.put({ schluessel, wert });
}

export const ABGLEICHSTAND = 'abgleichstand';
export const ANMELDUNG = 'anmeldung';

type Abgleichtabelle = Ausgangseintrag['tabelle'];

const TABELLEN = {
  projekte: () => db.projekte,
  flaechen: () => db.flaechen,
  belegungen: () => db.belegungen,
  fotos: () => db.fotos,
  strings: () => db.strings,
  projekt_komponenten: () => db.projekt_komponenten,
  leitungswege: () => db.leitungswege,
  materialpositionen: () => db.materialpositionen,
  wirtschaftlichkeit: () => db.wirtschaftlichkeit,
  verschattungsobjekte: () => db.verschattungsobjekte,
  kartenmarken: () => db.kartenmarken,
  projektschritte: () => db.projektschritte,
  dokumente: () => db.dokumente,
  auftraege: () => db.auftraege,
  pruefbefunde: () => db.pruefbefunde,
  projekteinstellungen: () => db.projekteinstellungen,
  // Stammdaten: schreibbar nur mit der passenden Rolle, geprueft im Server.
  module: () => db.module,
  komponenten: () => db.komponenten,
  artikel: () => db.artikel,
  materialsaetze: () => db.materialsaetze,
  einstellungen: () => db.einstellungen,
} as const;

/**
 * Lokal speichern und die Aenderung in die Ausgangswarteschlange legen
 * (PV-B12). Beides in einer Transaktion — sonst gibt es Datensaetze, von denen
 * der Server nie erfaehrt.
 *
 * Gespeichert wird auf den Stand, der *jetzt* in der Datenbank liegt, nicht auf
 * den, den der Aufrufer beim Zeichnen hatte. Zwei schnelle Tipps hintereinander
 * hatten sonst denselben alten Stand: Der zweite schrieb die Zeile vom ersten
 * zurueck, und das erste Modul war wieder im String. Wer ein Feld aus seinem
 * alten Wert rechnet (eine Liste ergaenzen), gibt statt der Felder eine
 * Funktion mit — sie bekommt die aktuelle Zeile.
 */
export async function speichere<T extends { seq: number | null }>(
  tabelle: Abgleichtabelle,
  datensatz: T & { id?: string; schluessel?: string },
  geaenderteFelder: Record<string, unknown> | ((aktuell: T) => Record<string, unknown>),
): Promise<void> {
  const jetzt = new Date().toISOString();
  // Einstellungen haengen am Schluessel, alles andere an der Kennung. Der
  // Server macht dieselbe Unterscheidung — sonst laendet die Aenderung in der
  // Warteschlange und findet auf der Gegenseite keine Zeile.
  const kennung = tabelle === 'einstellungen' ? datensatz.schluessel! : datensatz.id!;

  await db.transaction(
    'rw',
    TABELLEN[tabelle]() as Table<unknown, string>,
    db.ausgang,
    async () => {
      const tab = TABELLEN[tabelle]() as Table<unknown, string>;
      const aktuell = ((await tab.get(kennung)) as T | undefined) ?? datensatz;
      const felder = typeof geaenderteFelder === 'function' ? geaenderteFelder(aktuell) : geaenderteFelder;
      await tab.put({
        ...aktuell,
        ...felder,
        schmutzig: true,
        geaendert_am: jetzt,
      });
      // Mehrere Aenderungen an derselben Zeile werden zusammengefasst: es zaehlt
      // der Endstand, nicht der Weg dorthin.
      const offen = await db.ausgang.where('[tabelle+id]').equals([tabelle, kennung]).first();
      if (offen?.nummer !== undefined) {
        await db.ausgang.update(offen.nummer, {
          daten: { ...offen.daten, ...felder },
          angelegt_am: jetzt,
        });
      } else {
        await db.ausgang.add({
          tabelle,
          id: kennung,
          basisSeq: aktuell.seq,
          daten: felder,
          angelegt_am: jetzt,
        });
      }
    },
  );
}

export async function offeneAenderungen(): Promise<number> {
  return db.ausgang.count();
}

export function neueId(): string {
  return crypto.randomUUID();
}
